import Title from "atoms/Title";
import TabsList from "molecules/TabsList";
import React, { useEffect, useMemo, useState } from "react";
import {
  Form,
  notification,
  Modal,
  Spin,
  Row,
  Col,
  Select,
  DatePicker,
  Result,
} from "antd";
import Button from "atoms/Button";
import styled from "styled-components";
import ClientFields from "./ClientFields";
import InformationFields from "./InformationFields";
import StakeHolderFields from "./StakeHolderFields";
import FinancialResourcePlanFields from "./FinancialResourcePlanFields";
import JobCloserFields from "./JobCloserFields";
import { useMutate } from "hooks/useMutate";
import queryKeys from "common/queryKey";
import moment from "moment";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useUpdate } from "hooks/useUpdate";
import { useSelector, useDispatch } from "react-redux";
import {
  getUserInfo,
  getTempEngagementFormData,
  getUserList,
  getEngagementSelectedTab,
  getCurrencyList,
  getHigherOfficials,
  getMenuList,
  getUserRoleId,
} from "redux/selectors";
import axios from "axios";
import { sumBy } from "lodash";
import { useQueryClient } from "react-query";
import {
  setTempEngagementFormData,
  setselectedengagementtab,
  toggleProcessingModal,
  setResourceStatus,
} from "redux/actions";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useLocation } from "react-router-dom";
import { useScrollFetch2 } from "hooks/useScrollFetch2";
import { getEmailsByRole } from "utils/functions";

const labelStyle = {
  width: "150px",
  display: "inline-block",
  textAlign: "start",
};

const AddNewEngagement = ({ engagementData, update = false }) => {
  const dispatch = useDispatch();
  const [tabId, setTabId] = useState(1);
  const history = useHistory();
  const userInfo = useSelector((state) => getUserInfo(state));
  const userList = useSelector((state) => getUserList(state));
  // Menu permissions for the logged-in user's role
  const menuList = useSelector(getMenuList);
  const userRoleId = useSelector(getUserRoleId);
  const Menulist = menuList?.[userRoleId] || Object.values(menuList || {})[0];

  // Find the engagement menu entry (top-level or inside a subMenu)
  const engagementMenu = useMemo(() => {
    for (const item of Menulist || []) {
      if (item?.key === "engagement") return item;
      const sub = item?.subMenu?.find((s) => s?.key === "engagement");
      if (sub) return sub;
    }
    return null;
  }, [Menulist]);

  const hasEngagementCreatePermission = !!engagementMenu?.permission?.create;
  const hasEngagementEditPermission = !!engagementMenu?.permission?.edit;
  // Adding needs "create", updating an existing engagement needs "edit"
  const hasSavePermission = update ? hasEngagementEditPermission : hasEngagementCreatePermission;
  const [deletedbookedUserId, setDeletedbookedUserId] = React.useState([]);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isResource, setIsResource] = useState(false);

  const currencyLists = useSelector((state) => getCurrencyList(state));
  const currencyList = currencyLists[0]?.us_dollar;

  const tempEngagementFormData = useSelector((state) =>
    getTempEngagementFormData(state)
  );

  const selectedEngagementTab = useSelector((state) =>
    getEngagementSelectedTab(state)
  );
  const [tempEngagementId] = useState(Date.now());
  const formattedtempEngagementId = "PRJ" + tempEngagementId?.toString();
  const queryClient = useQueryClient();
  const location = useLocation();
  const paramsClone = new URLSearchParams(location.search);
  const isCloned = paramsClone.get('cloned') === 'true';
  const engagementId = engagementData?.id ?? formattedtempEngagementId;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const higherOfficialsList = useSelector((state) => getHigherOfficials(state));
  const engagementHigherUsers = getEmailsByRole(higherOfficialsList, "engagementHigherUsers");


  const handleBackClick = () => {
    dispatch(setselectedengagementtab(tabId));
    history.goBack();
    dispatch(setselectedengagementtab());
  };

  const handleTabSelect = (id) => {
    setTabId(Number(id));
    dispatch(setselectedengagementtab(id));
  };

  useEffect(() => {
    const storedTabId = selectedEngagementTab;
    if (storedTabId) {
      setTabId(Number(storedTabId));
    } else {
      setTabId(1);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (
      isCloned
    ) {
      setTabId(1);
    }
  }, [engagementData]);

  const [form] = Form.useForm();
  // FIX: Form.useWatch("job_title"/"client") doesn't reliably reflect the value
  // written by form.setFieldsValue(initialValues(...)) until that field's own
  // Form.Item has mounted. job_title lives in the Information tab and client
  // lives in the Client tab, so on first load — before the user switches tabs —
  // useWatch can lag behind and report the field as empty, permanently
  // disabling "Save as Draft" until a tab switch forces those Form.Items to
  // mount. Tracking the two values in local state instead (seeded as soon as
  // initialValues is computed, and kept in sync via onValuesChange) removes
  // that mount-order dependency entirely.
  const [jobTitleValue, setJobTitleValue] = useState("");
  const [clientValue, setClientValue] = useState(null);
  const isDraftRequiredFieldsFilled =
    !!jobTitleValue?.toString().trim() && !!clientValue?.toString().trim();
  // Draft button only makes sense for a new engagement, or an existing one still in Draft status.
  // Once an engagement has been properly Submitted (status moves off "Draft"), hide it.
  const showDraftButton = !update || engagementData?.jobCloser?.status === "Draft";
  const {
    addEngagement: addEngagementQuery,
    updateEngagement: updateEngagementQuery,
    postEngagementDocument,
    getEngagementDocument,
    getEngagementById,
    getEngagements,
    getEngagementDocumentList,
    postEngagementDocumentList,
  } = queryKeys;

  const { key: addEngagementKey, url: addEngagementUrl } =
    addEngagementQuery(false);

  const { mutate: addEngagement, isLoading: isPosting } = useMutate(
    addEngagementKey,
    addEngagementUrl
  );

  useEffect(() => {
    dispatch(toggleProcessingModal(isPosting));
  }, [isPosting]);

  const { mutate: updateEngagement, isLoading: isUpdating } = useUpdate(
    updateEngagementQuery.key,
    updateEngagementQuery.url
  );

  // const { key: getEngagementDocumentKey, url: getEngagementDocumentUrl } =
  //   getEngagementDocument(engagementId);
  const queryParams = new URLSearchParams();
  queryParams.append(
    "engagementid",
    engagementData?.engagementId || tempEngagementFormData?.engagement_id
  );

  const queryString = queryParams.toString();

  const { key: getEngagementDocumentKey, url: getEngagementDocumentUrl } =
    getEngagementDocumentList(engagementData?.engagementId, `?${queryString}`);

  const {
    data,
    lastElementRef,
    isLoading: isDocumentLoading,
    refetch: refetchDocuments,
  } = useScrollFetch2(
    getEngagementDocumentKey,
    {
      endPoint: getEngagementDocumentUrl,
      queryParam: "lastEvaluatedKey",
    },
    {
      enabled: !!engagementData?.engagementId,
    }
  );
  const dataList = useMemo(() => {
    if (data) {
      return data?.pages?.reduce((acc, page) => {
        if (page?.response?.items === undefined) return acc;
        return [...acc, ...page?.response?.items];
      }, []);
    } else {
      return [];
    }
  }, [data]);

  useEffect(() => {
    if (dataList) {
      const loeDocument = dataList?.filter((val) => val?.category === "LOE");
      const coiDocument = dataList?.filter((val) => val?.category === "COI");

      if (loeDocument.length > 0) {
        const loeDocumentFileList = loeDocument.map((doc) => ({
          url: doc.fileurl,
          name: doc.filename,
          categorydocid: doc.categorydocid,
          engagementid: doc.engagementid,
        }));

        form.setFieldValue("loe_document_upload", loeDocumentFileList);
      }

      if (coiDocument) {
        const coiDocumentFileList = coiDocument.map((doc) => ({
          url: doc.fileurl,
          name: doc.filename,
          categorydocid: doc.categorydocid,
          engagementid: doc.engagementid,
        }));
        form.setFieldValue("coi_document_upload", coiDocumentFileList);
      }
    }
  }, [dataList, form]);

  const { mutate: uploadLOEDocument, isLoading: isuploadLOEDocumentUploading } =
    useMutate(postEngagementDocument.key, postEngagementDocument.url);

  const { mutate: uploadCOIDocument, isLoading: isuploadCOIDocumentUploading } =
    useMutate(
      postEngagementDocument.key,
      postEngagementDocument.url + "?docType=COI"
    );

  const uploadImageToS3 = async (base64Data, url) => {
    if (!base64Data || !url) {
      notification.error({
        message: "Error uploading image to S3",
      });
    }
    try {
      const res = await axios.put(url, base64Data);

      setTimeout(() => {
        refetchDocuments();
      }, 1000);

      return res;
    } catch (error) {
      console.error("Error uploading image to S3", error);
    }
  };

  const { mutate: documentadd, isLoading: isdocumentPosting } = useMutate(
    postEngagementDocumentList.key,
    postEngagementDocumentList.url
  );

  const onUploadtoDocument = (file, base64, type) => {
    const payload = {
      filename: file?.name,
    };
    setUploading(true);
    uploadLOEDocument(payload, {
      onSuccess: async (res) => {
        if (res?.url) {
          try {
            const putRes = await fetch(res.url, {
              method: "PUT",
              headers: {
                "Content-Type": file.type || "application/octet-stream",
              },
              body: file,
            });
            if (putRes.ok) {
              const timestampid = res?.key?.split("/")[1] || null;
              const payloadNew = {
                clientid:
                  engagementData?.clientId || tempEngagementFormData?.client,
                engagementid:
                  engagementData?.documentId ||
                  engagementData?.engagementId ||
                  tempEngagementFormData?.engagement_id,
                category: type,
                filename: file?.name,
                attachmenttype: file?.type,
                description: "-",
                createdby: userInfo?.userid,
                updatedby: userInfo?.userid,
                key: res?.key,
                timestampid: timestampid,
              };
              documentadd(payloadNew, {
                onSuccess: (res) => {
                  notification.success({
                    message: "File uploaded successfully",
                  });
                },
              });
            } else {
              console.error("Failed to upload to S3 ❌", putRes.statusText);
              notification.error({ message: "File upload failed" });
            }
          } catch (err) {
            console.error("Error while uploading to S3 ❌", err);
          } finally {
            setUploading(false);
          }
        } else {
          console.error("No upload URL received from API");
          setUploading(false);
        }
      },
      onError: (err) => {
        console.error("Error creating presigned URL ❌", err);
        setUploading(false);
      },
    });
  };

  const handleClientUpdate = (isDisabled) => {
    setIsSubmitDisabled(isDisabled);
  };

  const handleUploadDocument = async (file, base64, type = false) => {
    // if (gotEngagementId) {
    //   // do nothing
    // } else if (!update) return; //? while adding new engagement, the document upload will be called only after the engagement is created
    const actualFile = file.originFileObj || file.file;
    onUploadtoDocument(actualFile, base64, type);
    // const data = {
    //   engagementId: engagementId || gotEngagementId,
    //   fileName: actualFile?.name,
    // };
    // if (docType === "LOE") {
    //   uploadLOEDocument(data, {
    //     onSuccess: (res) => {
    //       uploadImageToS3(actualFile, res?.url);
    //     },
    //   });
    // }
    // if (docType === "COI") {
    //   uploadCOIDocument(data, {
    //     onSuccess: (res) => {
    //       uploadImageToS3(actualFile, res?.url);
    //     },
    //   });
    // }
  };

  const handleValuesChange = (changedValues) => {
    // Keep the locally-tracked job_title/client in sync with live edits so
    // isDraftRequiredFieldsFilled (and the Save as Draft button) reacts
    // immediately, independent of Form.useWatch mount timing.
    if (changedValues.job_title !== undefined) {
      setJobTitleValue(changedValues.job_title);
    }
    if (changedValues.client !== undefined) {
      setClientValue(changedValues.client);
    }

    // Get all form values
    const formValues = form.getFieldsValue(true);
    // Check if specific fields have changed
    const isPartnerChanged = changedValues.partner !== undefined;
    const isRelationshipPartnerChanged =
      changedValues.relationship_partner !== undefined;
    const isPersonInchargeChanged = changedValues.person_incharge !== undefined;
    const isAuditor = changedValues.isauditor !== undefined;
    // Initialize the payload with the necessary values

    // Update the resourceList if any of the specified fields changed
    if (
      isPartnerChanged ||
      isPersonInchargeChanged ||
      isRelationshipPartnerChanged ||
      isAuditor
    ) {
      let seletedUserId =
        changedValues?.partner ||
        changedValues.relationship_partner ||
        changedValues.person_incharge ||
        changedValues.isauditor;
      let selectedResource = userList.find(
        (val) => val.userid === seletedUserId
      );

      const payload = {
        userId: selectedResource?.userid || null,
        employeeName: selectedResource?.fullname || null,
        employeeId: selectedResource?.employeeid || null,
        accessRole: form?.access_role || null,
        projectRole: isPartnerChanged
          ? "Partner"
          : isPersonInchargeChanged
            ? "Person Incharge"
            : isRelationshipPartnerChanged
              ? "QA Partner"
              : "Is Auditor",
        actualBookedStartDate: null,
        actualBookedEndDate: null,
        engagementId: tempEngagementFormData?.engagement_id,
        engagementName: tempEngagementFormData?.job_title || null,
        clientId: tempEngagementFormData?.client || null,
        effort: 0,
        remarks: null,
        personDay: null,
        resourceExtRate: selectedResource?.chargeoutrate || null,
        status: "Assigned",
        engagementStartDate: null,
        engagementEndDate: null,
        weekList: null,
        justification: null,
        managingOffice: tempEngagementFormData?.managing_office,
        competency: tempEngagementFormData?.client_competency ?? null,
        serviceLine: tempEngagementFormData?.service_line ?? null,
      };

      const currentResourceList = tempEngagementFormData?.resourceList || [];

      // Check if the resourceList already contains an object with the same projectRole
      const updatedResourceList = currentResourceList.map((item) =>
        item.projectRole === payload.projectRole ? payload : item
      );

      // If the payload's projectRole does not match any existing item, add it
      if (
        !currentResourceList.some(
          (item) => item.projectRole === payload.projectRole
        )
      ) {
        updatedResourceList.push(payload);
      }

      // Update the formValues with the new resourceList
      const updatedFormValues = {
        ...formValues,
        resourceList: updatedResourceList,
        subcontractList: tempEngagementFormData?.subcontractList || [],
      };

      updatedFormValues.subcontractList =
        tempEngagementFormData?.subcontractList || [];
      // Dispatch updated form values to Redux
      const safeValues = { ...updatedFormValues };

      // 🚫 status must NEVER come from temp form
      delete safeValues.status;

      dispatch(setTempEngagementFormData(safeValues));
    } else {
      // Update subcontractList in formValues with subcontractList from tempEngagementFormData
      formValues.subcontractList =
        tempEngagementFormData?.subcontractList || [];
      const updatedFormValues = {
        ...formValues,
        resourceList: tempEngagementFormData?.resourceList || [],
        subcontractList: tempEngagementFormData?.subcontractList || [],
      };
      // Dispatch the original form values if no specific fields changed
      dispatch(setTempEngagementFormData(updatedFormValues));
    }
  };

  useEffect(() => {
    const filteruser = userList.find(
      (val) => val?.userid === userInfo?.userid
    );
    // FIX: Always pass tempEngagementFormData so user-entered billing_amount /
    // conversion_rate / usd_amount are preserved when returning from add-resources.
    // Previously engagementData branch passed {} which wiped all unsaved financial fields.
    const computedInitialValues = initialValues(
      engagementData ?? {},
      tempEngagementFormData,
      formattedtempEngagementId,
      form,
      filteruser,
      userList
    );
    form.setFieldsValue(computedInitialValues);
    // Seed the local job_title/client tracking state directly from the values
    // we just computed, rather than waiting on Form.useWatch to catch up once
    // those fields' Form.Items mount (see isDraftRequiredFieldsFilled above).
    setJobTitleValue(computedInitialValues.job_title);
    setClientValue(computedInitialValues.client);
  }, [engagementData, form]);

  // Mirrors the isSuperAdmin check in EngagementView/index.js (role "383") — a super
  // admin can always view and edit any engagement, regardless of stakeholder/creator status.
  const isSuperAdmin = useMemo(() => {
    const loggedInUser = userList?.find((val) => val?.userid === userInfo?.userid);
    return ["2"].includes(loggedInUser?.roleid?.[0]);
  }, [userList, userInfo]);

  const isFormDisable = () => {
    if (isSuperAdmin) {
      return false;
    }

    const engagementStatus = engagementData?.jobCloser?.status;

    if (update && engagementStatus === "Draft") {
      return false;
    }

    const userDetails = userList.find(
      (val) => val?.userid === userInfo?.userid
    );

    const userManaginOffice = userDetails?.managingoffice?.[0];

    if (engagementStatus === "Active" || engagementStatus === "Closed") {
      return true;
    }

    const isPartnerOrManagerOrHeadOfficeUser =
      userInfo?.userid === engagementData?.stakeHolder?.personIncharge ||
      userInfo?.userid === engagementData?.stakeHolder?.partner ||
      userManaginOffice === "Head Office" || userInfo?.roleId === "8";
    return update ? !isPartnerOrManagerOrHeadOfficeUser : false;
  };
  const isAddResorceAndSubDisable = () => {
    if (isSuperAdmin) {
      return false;
    }
    // const engagementStatus = engagementData?.jobCloser?.status;

    // if (engagementStatus === "Active") {
    //   return true;
    // }

    const isPartnerOrManager =
      userInfo?.userid === engagementData?.stakeHolder?.personIncharge ||
      userInfo?.userid === engagementData?.stakeHolder?.partner;
    return update ? !isPartnerOrManager : false;
  };

  const isLocalAddresourceDisable = () => {
    if (isSuperAdmin) {
      return false;
    }
    if (update && engagementData?.jobCloser?.status === "Draft") {
      return false;
    }
    const isPartnerOrManager =
      userInfo?.userid === engagementData?.stakeHolder?.personIncharge ||
      userInfo?.userid === engagementData?.stakeHolder?.partner ||
      engagementHigherUsers?.includes(
        userInfo?.userid
      ) || userInfo?.roleId === "8";
    return update ? !isPartnerOrManager : false;
  };

  const localAddresourceDisable = !hasSavePermission || isLocalAddresourceDisable();

  const addResourceandSubDisabled = !hasSavePermission || isAddResorceAndSubDisable();

  const formdisabled = !hasSavePermission || isFormDisable();

  const getActiveChargeOut = (userId, fallback = 0) => {
    const user = userList.find(
      u => u.userid?.toLowerCase() === userId?.toLowerCase()
    );
    return Number(user?.chargeoutrate ?? fallback ?? 0);
  };

  const handleSubmit = (values, options = {}) => {
    const { isDraft = false } = options;
    return new Promise((resolve, reject) => {
      if (!hasSavePermission) {
        notification.error({ message: "You don't have permission to perform this action" });
        return reject(new Error("No permission"));
      }
      if (update) {
        const formAttributes = formsToAttributes(form.getFieldsValue(true), {}, form);
        const updatedData = {
          ...engagementData,
          ...formAttributes,
          stakeHolder: {
            ...engagementData?.stakeHolder,
            ...formAttributes.stakeHolder,
          },
          deletedResourceIds: deletedbookedUserId,
        };

        const updateResourceData = () => {
          let filteredList = [];

          if (tempEngagementFormData?.resourceList?.length > 0) {
            filteredList = [...tempEngagementFormData.resourceList];

            if (tempEngagementFormData?.relationship_partner === undefined) {
              filteredList = tempEngagementFormData?.resourceList?.filter(
                (item) => item.projectRole !== "QA Partner"
              );
            }

            if (tempEngagementFormData?.isauditor === undefined) {
              filteredList = tempEngagementFormData?.resourceList?.filter(
                (item) => item.projectRole !== "Is Auditor"
              );
            }

            if (
              tempEngagementFormData?.relationship_partner === undefined &&
              tempEngagementFormData?.isauditor === undefined
            ) {
              filteredList = tempEngagementFormData?.resourceList?.filter(
                (item) =>
                  item.projectRole !== "QA Partner" &&
                  item.projectRole !== "Is Auditor"
              );
            }
          }
          const merged = [
            ...(updatedData?.employeeList || []).map(res => {
              if (!res.userId) return res;

              const user = userList.find(
                u => u.userid?.toLowerCase() === res.userId?.toLowerCase()
              );

              const isActiveJob =
                engagementData?.jobCloser?.status === "Active";

              return {
                ...res,
                resourceExtRate: isActiveJob
                  ? Number(user?.chargeoutrate ?? 0)
                  : Number(res.resourceExtRate ?? 0),
              };
            }),
            ...(filteredList || []),
          ];
          // FIX: entries freshly built in handleValuesChange (Partner/Relationship
          // Partner/Person Incharge/Is Auditor) never carry a `resourceId` — it's
          // only assigned once a resource is actually booked with an id server-side.
          // Deduping strictly on `r.resourceId` meant every such entry collided on
          // the same `undefined` key, so changing e.g. both Partner and Person
          // Incharge in one edit silently dropped all but the last of them here.
          // Fall back to a stable role+user key so distinct roles never collide.
          const dedupeKey = (r) => r.resourceId ?? `${r.projectRole}::${r.userId}`;
          return Array.from(
            new Map(merged.map(r => [dedupeKey(r), r])).values()
          );
        };
        const finalResourceList = updateResourceData();

        // 🔥 SYNC employeeList FROM resourceList
        updatedData.resourceList = finalResourceList;
        // FIX: this used to be `(updatedData.employeeList || []).map(...)`, which
        // only refreshes resourceExtRate on entries that ALREADY existed in the
        // engagement's saved employeeList — it never copies in brand-new entries
        // from finalResourceList. So the first save (add path) worked because
        // employeeList was seeded from resourceList directly, but re-editing an
        // existing draft and changing Partner/Person Incharge again went through
        // this update path, remapped only the (possibly still-empty) old
        // employeeList, and the newly changed resources never made it in —
        // producing employeeList: [] whenever the saved draft's employeeList
        // didn't already contain those roles. employeeList and resourceList
        // should simply be kept in sync as the same merged list.
        updatedData.employeeList = finalResourceList;
        const resolvedSubmitStatus =
          form.getFieldValue("status") || engagementData?.jobCloser?.status;
        updatedData.jobCloser = {
          ...updatedData.jobCloser,
          status: isDraft
            ? "Draft"
            : (resolvedSubmitStatus === "Draft" ? "Not Started" : resolvedSubmitStatus),
        };

        updateEngagement(updatedData, {
          onSuccess: (res) => {
            if (res.status) {
              notification.success({
                message: res?.message,
              });
              dispatch(setTempEngagementFormData(""));
              if (isDraft) {
                history.goBack();
              }
            } else {
              console.error("[Draft Debug] Edit submit rejected by API:", res?.message);
              form?.setFieldValue("status", engagementData?.jobCloser?.status);
              notification.error({
                message: res?.message,
              });
              showValidationErrorModal(res.message);
            }
            queryClient.invalidateQueries(getEngagementById(engagementId).key);
            queryClient.invalidateQueries(getEngagements.key);
            resolve(); // resolve the promise here
          },
          onError: (error) => {
            console.error("[Draft Debug] Edit submit request failed:", error);
            notification.error({
              message: error?.message || "Failed to save engagement. Please try again.",
            });
            reject(error); // reject if there's an error
          },
        });
      } else {
        const data = {
          ...formsToAttributes(
            tempEngagementFormData,
            tempEngagementFormData,
            form
          ),
          // FIX: formsToAttributes only ever populates resourceList/subcontractList
          // on its returned object — it never sets employeeList. On the UPDATE path
          // that's fine because employeeList is rebuilt separately a few lines above
          // (synced from resourceList against the existing engagementData.employeeList).
          // But on this ADD (new engagement) path there is no such follow-up step, so
          // employeeList was never set at all and defaulted to [] server-side even
          // when resources had been booked in the Financial & Resource Plan tab.
          // Mirror resourceList into employeeList here so newly booked resources are
          // actually persisted when creating a brand-new engagement.
          employeeList: tempEngagementFormData?.resourceList ?? [],
          createdBy: userInfo?.userid,
        };
        if (isDraft) {
          data.jobCloser = { ...data.jobCloser, status: "Draft" };
        } else if (data.jobCloser?.status === "Draft") {
          data.jobCloser = { ...data.jobCloser, status: "Not Started" };
        }

        if (
          isDraft ||
          (tempEngagementFormData?.job_title &&
            tempEngagementFormData?.partner &&
            tempEngagementFormData?.billing_office)
        ) {
          addEngagement(data, {
            onSuccess: (res) => {
              if (res.status) {
                notification.success({
                  message: isDraft
                    ? "Engagement Saved as Draft"
                    : "Engagement Added Successfully",
                });
                dispatch(setTempEngagementFormData(""));
                if (isDraft) {
                  history.goBack();
                } else {
                  history.push("/engagement");
                }
                resolve();
              } else {
                // Handle API validation errors
                console.error("[Draft Debug] Add submit rejected by API:", res?.message);
                showValidationErrorModal(res.message);
              }
            },
            onError: (error) => {
              console.error("[Draft Debug] Add submit request failed:", error);
              notification.error({
                message: error?.message || "Failed to save engagement. Please try again.",
              });
              reject(error); // reject if there's an error
            },
          });
        } else {
          notification.error({
            message: "Please fill all the required fields",
          });
        }
      }
    });
  };

  const showValidationErrorModal = (message) => {
    setValidationError(message);
  };

  // Returns true once the save has actually gone through (or been handed
  // off to the pfa_status confirm), false if form validation failed - so
  // callers like StakeHolderFields' partner/personIncharge reassignment can
  // tell the difference and revert their own field on a validation failure.
  const submitEngagement = async () => {
    let values;
    try {
      // Validate the form before showing the modal
      values = await form.validateFields();
    } catch (validationError) {
      notification.error({ message: "Please fill the mandatory fields" });
      return false;
    }
    try {
      if (!update && values?.pfa_status === "Not Approved") {
        Modal.confirm({
          icon: "",
          content: (
            <div style={{ textAlign: "center" }}>
              <p>
                This engagement requires approval from the partner. It will not
                appear in the timesheet until the partner has given their
                approval.
              </p>
              <p>Would you like to proceed?</p>
            </div>
          ),
          okText: (
            <span>{isSubmitting ? <Spin size="small" /> : null} YES</span>
          ),
          cancelText: "No",
          onOk: async () => {
            setIsSubmitting(true);
            try {
              await handleSubmit(values);
            } finally {
              setIsSubmitting(false);
            }
          },
          onCancel: () => {
            setIsSubmitting(false);
          },
        });
      } else {
        try {
          await handleSubmit(values);
        } finally {
          setIsResource(false);
          dispatch(setResourceStatus(false));
        }
      }
      return true;
    } catch (error) {
      console.error("[submitEngagement] error -->", error);
      setIsSubmitting(false);
      return false;
    }
  };

  const submitAsDraft = async () => {
    if (!isDraftRequiredFieldsFilled) {
      notification.error({
        message: "Please select Client and enter Job Title before saving as draft",
      });
      return;
    }
    setIsSavingDraft(true);
    try {
      const values = form.getFieldsValue(true);
      await handleSubmit(values, { isDraft: true });
    } catch (error) {
      console.error("[submitAsDraft] error -->", error);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const activeUserslist = userList.filter((item) => item.statusid === "1");
  // const userListOptions = userList
  //   .filter(
  //     (user) =>
  //       user?.designation === "Managing Partner" ||
  //       user?.designation === "Partner" ||
  //       user?.designation === "Senior Partner"
  //   )
  //   .map((user) => ({
  //     label: user.fullname,
  //     value: user.userid,
  //   }));

  const allUserListOptions = activeUserslist.map((user) => {
    return {
      label: user.fullname,
      value: user.userid,
    };
  });

  const [showCloseEngagementModal, setShowCloseEngagementModal] =
    useState(false);

  const partnerUser = userList.find(
    (user) => user.userid === engagementData?.stakeHolder?.partner
  );
  const personInchargeUser = userList.find(
    (user) => user.userid === engagementData?.stakeHolder?.personIncharge
  );

  let closedByOptions = [];
  if (
    partnerUser &&
    personInchargeUser &&
    partnerUser.userid === personInchargeUser.userid
  ) {
    closedByOptions = [
      {
        label: `${partnerUser.fullname}`,
        value: partnerUser.userid,
      },
    ];
  } else {
    if (partnerUser) {
      closedByOptions.push({
        label: `${partnerUser.fullname}`,
        value: partnerUser.userid,
      });
    }
    if (personInchargeUser) {
      closedByOptions.push({
        label: `${personInchargeUser.fullname}`,
        value: personInchargeUser.userid,
      });
    }
  }

  const handleCloseEngagementViaModal = async () => {
    form.setFieldsValue({ closeddate: moment() });
    setShowCloseEngagementModal(true);
  };

  const handleOkCloseEngagement = async () => {
    // FutureFeature
    // if (!engagementData?.checkList?.timesheet) {
    //   Notification.error({
    //     message: "Timesheet is not completed",
    //   });
    //   setShowCloseEngagementModal(false);
    //   return;
    // }
    // if (!engagementData?.checkList?.billing) {
    //   Notification.error({
    //     message: "Billing is not completed",
    //   });
    //   setShowCloseEngagementModal(false);
    //   return;
    // }
    // if (!engagementData?.checkList?.clientsurvey) {
    //   Notification.error({
    //     message: "Client Survey is not completed",
    //   });
    //   setShowCloseEngagementModal(false);
    //   return;
    // }
    // if (!engagementData?.checkList?.appraisal) {
    //   Notification.error({
    //     message: "Appraisal is not completed",
    //   });
    //   setShowCloseEngagementModal(false);
    //   return;
    // }

    setIsSubmitting(true);
    try {
      const values = await form.validateFields([
        "closeddate",
        "closedby",
        "close_comment"
      ]);
      await handleSubmit(values);
      setShowCloseEngagementModal(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error("[handleCloseEngagement] error -->", error);
      notification.error({ message: "Error closing engagement" });
      notification.error({ message: "Kindly fill all the required fields" });
      setIsSubmitting(false);
    }
  };

  const handleCancelCloseEngagementModal = () => {
    setIsSubmitting(false);
    setShowCloseEngagementModal(false);
    form.setFieldsValue({ status: "Active" });
    form.setFieldsValue({ close_comment: "" });
  };

  const items = [
    {
      id: 1,
      label: "Client",
      component: (
        <ClientFields
          onClientUpdate={handleClientUpdate}
          isDocumentLoading={isDocumentLoading || isuploadLOEDocumentUploading || isuploadCOIDocumentUploading || isdocumentPosting || uploading}
          handleUploadDocument={handleUploadDocument}
          engagementID={engagementData?.engagementId}
          engagementData={engagementData}
          update={update}
          disabled={formdisabled}
          noEditAccess={!hasSavePermission}
        />
      ),
    },
    {
      id: 2,
      label: "Information",
      component: (
        <InformationFields
          disabled={formdisabled}
          noEditAccess={!hasSavePermission}
          update={update}
          engagementData={engagementData}
          userInfo={userInfo}
          engagementHigherUsers={engagementHigherUsers}
        />
      ),
    },
    {
      id: 3,
      label: "Stake Holder",
      component: (
        <StakeHolderFields
          update={update}
          engagementData={engagementData}
          disabled={formdisabled}
          noEditAccess={!hasSavePermission}
          isPersonInchargeChanged={location?.state?.personInCharge}
          isPersonInchargeChangedPath={location?.pathname}
          submitEngagement={submitEngagement}
        />
      ),
    },
    {
      id: 4,
      label: "Financial & Resource Plan",
      component: (
        <FinancialResourcePlanFields
          update={update}
          setDeletedbookedUserId={setDeletedbookedUserId}
          disabled={formdisabled}
          noEditAccess={!hasSavePermission}
          addResourceandSubDisabled={addResourceandSubDisabled}
          localAddresourceDisable={localAddresourceDisable}
          tabId={tabId}
          setIsResource={setIsResource}
          isResource={isResource}
        />
      ),
    },
    {
      id: 5,
      label: "Job Closure",
      component: (
        <JobCloserFields
          update={update}
          handleCloseEngagementViaModal={handleCloseEngagementViaModal}
          disabled={formdisabled}
          noEditAccess={!hasSavePermission}
          validationError={validationError}
        />
      ),
    },
  ];

  useEffect(() => {
    if (
      !engagementData?.financial_resourcePlan?.conversion_rate &&
      !tempEngagementFormData?.conversion_rate &&
      currencyList?.us_dollar
    ) {
      form.setFieldsValue({
        conversion_rate: Number(currencyList?.us_dollar)
      });
    }
  }, [currencyList, engagementData, tempEngagementFormData]);

  if (!update && !hasEngagementCreatePermission) {
    return (
      <Result
        status="403"
        title="403"
        subTitle="Sorry, you don't have permission to create an engagement."
        extra={
          <Button type="primary" onClick={() => history.push("/engagement")}>
            Go to Engagement List
          </Button>
        }
      />
    );
  }

  return (
    <AddEngagementWrap>
      {!update && (
        <div style={{ display: "flex" }}>
          <div>
            <label
              className="bg-transparent border-0 mb-3 text-left styled-cursor"
              onClick={handleBackClick}
            >
              <span className="">Back</span>{" "}
              <ArrowForwardIosIcon className="px-1" />
            </label>{" "}
          </div>
          <div>{!update && <Title>Add New Engagement</Title>}</div>
        </div>
      )}
      <Form
        initialValues={initialValues(
          engagementData,
          tempEngagementFormData,
          formattedtempEngagementId,
          form,
          {},
          userList,
          currencyList
        )}
        onValuesChange={handleValuesChange}
        layout="horizontal"
        form={form}
        // PERMISSION FIX: without permission every field is read-only (greyed out)
        disabled={!hasSavePermission}
        // onFinish={handleSubmit}
        colon={false}
        requiredMark={false}
        onFinishFailed={() => {
          notification.error({
            message: "Please fill all the required fields",
          });
        }}
      >
        <TabsList
          items={items}
          handleTabSelect={handleTabSelect}
          tabId={tabId}
          forceRender={true}
        />
        {!localAddresourceDisable && (
          <div className="form-footer">
            <Button
              type="primary"
              htmlType="submit"
              loading={(isPosting || isUpdating) && !isSavingDraft}
              onClick={() => submitEngagement()}
              disabled={
                isSubmitDisabled ||
                isSavingDraft ||
                (update
                  ? engagementData?.jobCloser?.status === "Closed"
                  : false)
              }
            >
              Save
            </Button>
            {showDraftButton && (() => {
              const draftDisabled =
                isSubmitDisabled ||
                !isDraftRequiredFieldsFilled ||
                ((isPosting || isUpdating) && !isSavingDraft) ||
                (update
                  ? engagementData?.jobCloser?.status === "Closed"
                  : false);
              return (
                <Button
                  type="primary"
                  loading={isSavingDraft}
                  onClick={() => submitAsDraft()}
                  title={!isDraftRequiredFieldsFilled ? "Please select Client and enter Job Title before saving as draft" : undefined}
                  disabled={draftDisabled}
                >
                  Save as Draft
                </Button>
              );
            })()}
            <Button
              type="primary"
              onClick={() => {
                form.resetFields();
                dispatch(setTempEngagementFormData(""));
                history.push("/engagement");
              }}
            >
              Close
            </Button>
          </div>
        )}
      </Form>

      <Modal
        title="Close Engagement"
        open={showCloseEngagementModal}
        onOk={handleOkCloseEngagement}
        onCancel={handleCancelCloseEngagementModal}
        okText={isSubmitting ? <Spin size="small" /> : "Yes"}
        cancelText="No"
      >
        <div>
          <p
            style={{
              fontWeight: "500",
              marginBottom: "16px",
            }}
          >
            Are you sure you want to close this engagement?
          </p>

          <Form form={form} colon={false}>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  label={<span style={labelStyle}>Closed On</span>}
                  name="closeddate"
                  rules={[
                    { required: true, message: "Please enter Closed Date!" },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format={"DD-MM-YYYY"}
                    disabledDate={(current) =>
                      current && current < moment().endOf("day")
                    }
                    disabled={true}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label={<span style={labelStyle}>Closed By</span>}
                  name="closedby"
                  rules={[
                    {
                      required: true,
                      message: "Please select who closed the engagement!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select"
                    showSearch={true}
                    filterOption={(input, option) =>
                      (option?.label?.toLowerCase() ?? "").includes(
                        input.toLowerCase()
                      )
                    }
                    options={closedByOptions}
                    disabled={false}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label={<span style={labelStyle}>Comment</span>}
                  name="close_comment"
                  rules={[
                    {
                      required: true,
                      message: "Please enter a comment!",
                    },
                  ]}
                >
                  <textarea
                    className="ant-input"
                    style={{ width: "100%", minHeight: 60, resize: "vertical" }}
                    placeholder="Enter comment"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>
    </AddEngagementWrap>
  );
};

export default AddNewEngagement;

export const Required = () => {
  return <span style={{ color: "red" }}>*</span>;
};

const AddEngagementWrap = styled.div`
  .fields-card {
    box-shadow: 0px 3px 6px #00000015;
    border-radius: 5px;
  }
  .ant-tabs-tab {
    padding: 5px 0;
  }
  .ant-tabs-nav {
    color: #000000;
  }
  .ant-tabs-top > .ant-tabs-nav::before {
    border-bottom: none;
  }
  .ant-tabs-nav {
    background-color: #f3fefa;
  }
  .ant-form-item {
    margin-bottom: 14px;
  }
  .profit {
    color: #4caf50;
    font-weight: 600;
  }
  .loss {
    color: #f44336;
    font-weight: 600;
  }
  .ant-picker {
    width: 100%;
  }
  .form-footer {
    display: flex;
    justify-content: flex-start;
    margin-top: 20px;
  }
  .max-char-info {
    font-size: 12px;
    color: #bfbfbf;
    margin-top: -8px;
    text-align: end;
  }
  .ant-checkbox.ant-checkbox-checked {
    .ant-checkbox-inner {
      background-color: #02b209 !important;
      border-color: #02b209 !important;
    }
  }
  .ant-upload.ant-upload-select.ant-upload-select-text {
    width: 100%;
  }
  .job-closer-buttons-container {
    margin-left: 70px;

    @media (max-width: 768px) {
      margin-left: 0;
    }
  }

  .job-closer-buttons-container .ant-btn-primary {
    border-radius: 5px;
    font-weight: 500;
  }

  .client-name-id {
    display: flex;
    justify-content: start;
    gap: 50px;
    flex-wrap: wrap;

    .client-name {
      font-weight: 600;
    }
  }

  .form-footer .ant-btn-primary {
    border-radius: 5px;
    font-weight: 500;
  }
`;

export const initialValues = (
  engagementData,
  tempEngagementFormData,
  formattedtempEngagementId,
  form,
  filteruser,
  userList,
  currencyList
) => {
  let subcontractoSum =
    sumBy(
      engagementData?.subcontractor ??
      parseInt(tempEngagementFormData?.subcontractor_usd),
      "amount"
    ) ?? "0.00";
  const totalusdamount =
    parseFloat(tempEngagementFormData?.billing_amount ?? 0) *
    parseFloat(tempEngagementFormData?.conversion_rate ?? 0) || "0.00";
  const loeDocument = form.getFieldValue("loe_document_upload");
  const coiDocumnet = form.getFieldValue("coi_document_upload");
  const clientName = form.getFieldValue("client_name");
  form.setFieldValue(
    "relationship_partner",
    tempEngagementFormData?.relationship_partner ??
    engagementData?.stakeHolder?.relationshipPartner
  );
  form.setFieldValue(
    "isauditor",
    tempEngagementFormData?.isauditor ?? engagementData?.stakeHolder?.isauditor
  );
  form.setFieldValue(
    "referring_bdo_firm",
    tempEngagementFormData?.referring_bdo_firm ??
    engagementData?.stakeHolder?.referringBdoFirm
  );
  form.setFieldValue(
    "other_side_entity_name",
    tempEngagementFormData?.other_side_entity_name ??
    engagementData?.stakeHolder?.otherSideEntityName
  );
  form.setFieldValue(
    "currency",
    tempEngagementFormData?.currency ??
      engagementData?.financial_resourcePlan?.currency ? tempEngagementFormData?.currency ??
    engagementData?.financial_resourcePlan?.currency : "US Dollar"
  );
  form.setFieldValue(
    "billing_amount",
    tempEngagementFormData?.billing_amount ??
    engagementData?.financial_resourcePlan?.billingAmount
  );
  const selectedCurrency = tempEngagementFormData?.currency ?? engagementData?.financial_resourcePlan?.currency ?? "US Dollar";
  const currencyKey = selectedCurrency.toLowerCase().replace(/\s+/g, "_");
  let conversionRate = 1;

  if (currencyList && typeof currencyList === "object" && !Array.isArray(currencyList)) {
    conversionRate = currencyList?.[currencyKey] ?? 1;
  }
  if (typeof currencyList === "number") {
    conversionRate = currencyList;
  }

  if (
    !tempEngagementFormData?.conversion_rate &&
    !engagementData?.financial_resourcePlan?.conversion_rate
  ) {
    form.setFieldValue("conversion_rate", currencyList);
  }

  return {
    engagement_id: engagementData?.engagementId
      ? engagementData?.engagementId
      : tempEngagementFormData?.engagement_id ?? formattedtempEngagementId,
    proposal_number:
      engagementData?.client?.proposalNumber ??
      tempEngagementFormData?.proposal_number ??
      "",
    managing_office:
      engagementData?.client?.managingOffice ??
      tempEngagementFormData?.managing_office ??
      filteruser?.managingoffice?.toString() ??
      null,
    department:
      engagementData?.client?.department ??
      tempEngagementFormData?.department ??
      null,
    client_competency:
      engagementData?.client?.competency ??
      tempEngagementFormData?.client_competency ??
      null,
    // FIX: the saved engagement carries the client id at the TOP LEVEL
    // (engagementData.clientId) — this is what ClientFields itself reads
    // to decide between its read-only / editable branches. The nested
    // engagementData?.client?.clientId path below doesn't exist in the
    // real data shape, so on edit this always fell through to
    // tempEngagementFormData?.client (usually empty on a fresh page load),
    // leaving `client` null. That made watchedClient falsy, which made
    // isDraftRequiredFieldsFilled false, which permanently disabled the
    // "Save as Draft" button even though the draft already had a client.
    client:
      engagementData?.clientId ??
      engagementData?.client?.clientId ??
      tempEngagementFormData?.client ??
      null,
    service_line:
      engagementData?.client?.serviceLine ??
      tempEngagementFormData?.service_line ??
      null,
    client_fy_month:
      engagementData?.client?.clientFyMonth ??
      tempEngagementFormData?.client_fy_month ??
      null,
    apt_engagement_id:
      engagementData?.client?.aptengagementid ??
      tempEngagementFormData?.apt_engagement_id ??
      null,
    loe_ref_no:
      engagementData?.client?.loeRefNo ??
      tempEngagementFormData?.loe_ref_no ??
      null,
    client_name:
      engagementData?.client?.clientName ??
      tempEngagementFormData?.client_name ??
      "",
    loe_document_upload: loeDocument?.length
      ? loeDocument
      : tempEngagementFormData?.loe_document_upload ?? [],
    coi_document_upload: coiDocumnet?.length
      ? coiDocumnet
      : tempEngagementFormData?.coi_document_upload ?? [],
    job_title:
      engagementData?.information?.jobTitle ??
      tempEngagementFormData?.job_title ??
      "",
    approved_date: engagementData?.information?.approvedDate
      ? moment(engagementData?.information?.approvedDate, "DD-MM-YYYY")
      : tempEngagementFormData?.approved_date
        ? moment(tempEngagementFormData?.approved_date, "DD-MM-YYYY")
        : moment(),
    classification:
      engagementData?.information?.classification ??
      tempEngagementFormData?.classification ??
      null,
    stage:
      engagementData?.information?.stage ??
      tempEngagementFormData?.stage ??
      null,
    country:
      engagementData?.information?.country ??
      tempEngagementFormData?.country ??
      null,
    objective:
      engagementData?.information?.objective ??
      tempEngagementFormData?.objective ??
      "",
    partner:
      engagementData?.stakeHolder?.partner ??
      tempEngagementFormData?.partner ??
      null,
    relationship_partner:
      engagementData?.stakeHolder?.relationshipPartner ??
      tempEngagementFormData?.relationship_partner ??
      null,
    person_incharge:
      (engagementData?.stakeHolder?.personIncharge ||
        engagementData?.stakeHolder?.PersonInCharge) ??
      tempEngagementFormData?.person_incharge ??
      null,
    referring_bdo_firm:
      engagementData?.stakeHolder?.referringBdoFirm ??
      tempEngagementFormData?.referring_bdo_firm ??
      null,
    other_side_entity_name:
      engagementData?.stakeHolder?.otherSideEntityName ??
      tempEngagementFormData?.other_side_entity_name ??
      null,
    sent_to_referaal:
      engagementData?.stakeHolder?.sentToReferral ??
      tempEngagementFormData?.sent_to_referaal ??
      "",
    plan_start_date: engagementData?.stakeHolder?.planStartDate
      ? moment(engagementData?.stakeHolder?.planStartDate, "DD-MM-YYYY")
      : tempEngagementFormData?.plan_start_date
        ? moment(tempEngagementFormData?.plan_start_date, "DD-MM-YYYY")
        : "",
    plan_end_date: engagementData?.stakeHolder?.planEndDate
      ? moment(engagementData?.stakeHolder?.planEndDate, "DD-MM-YYYY")
      : tempEngagementFormData?.plan_end_date
        ? moment(tempEngagementFormData?.plan_end_date, "DD-MM-YYYY")
        : "",
    client_fy_start: engagementData?.stakeHolder?.clientFyStart
      ? moment(engagementData?.stakeHolder?.clientFyStart, "DD-MM-YYYY")
      : tempEngagementFormData?.client_fy_start
        ? moment(tempEngagementFormData?.client_fy_start, "DD-MM-YYYY")
        : "",
    client_fy_end: engagementData?.stakeHolder?.clientFyEnd
      ? moment(engagementData?.stakeHolder?.clientFyEnd, "DD-MM-YYYY")
      : tempEngagementFormData?.client_fy_end
        ? moment(tempEngagementFormData?.client_fy_end, "DD-MM-YYYY")
        : "",
    isauditor:
      engagementData?.stakeHolder?.isauditor ??
      tempEngagementFormData?.isauditor ??
      null,
    billing_office:
      engagementData?.financial_resourcePlan?.billingOffice ??
      tempEngagementFormData?.billing_office ??
      null,
    financial_year:
      engagementData?.financial_resourcePlan?.financialYear ??
      tempEngagementFormData?.financial_year ??
      new Date().getFullYear(),
    mandays:
      sumBy(engagementData?.employeeList, (item) =>
        parseFloat(item.personDay ?? 0)
      ) ??
      tempEngagementFormData?.mandays ??
      null,
    currency:
      engagementData?.financial_resourcePlan?.currency ??
      tempEngagementFormData?.currency ??
      "US Dollar",
    billing_amount:
      engagementData?.financial_resourcePlan?.billingAmount ??
      tempEngagementFormData?.billing_amount ??
      "",
    // FIX: The original expression used ?? after a multiplication — ?? only catches
    // null/undefined, NOT NaN. When either operand is missing, the result is NaN
    // and the fallback never fires, leaving the field blank/0.
    // Now we compute safely and fall back to tempEngagementFormData's cached value.
    usd_amount: (() => {
      const ba = parseFloat(
        String(engagementData?.financial_resourcePlan?.billingAmount || "0").replace(/,/g, "")
      );
      const cr = parseFloat(
        String(engagementData?.financial_resourcePlan?.conversion_rate || "0").replace(/,/g, "")
      );
      const computed = ba * cr;
      if (!isNaN(computed) && computed !== 0) return computed;
      // Fall back to temp cache value (covers unsaved new-engagement flow)
      const tempBa = parseFloat(String(tempEngagementFormData?.billing_amount || "0").replace(/,/g, ""));
      const tempCr = parseFloat(String(tempEngagementFormData?.conversion_rate || "0").replace(/,/g, ""));
      const tempComputed = tempBa * tempCr;
      if (!isNaN(tempComputed) && tempComputed !== 0) return tempComputed;
      return "";
    })(),
    planned_fee_adjustment:
      engagementData?.financial_resourcePlan?.plannedFeeAdjustment ??
      tempEngagementFormData?.planned_fee_adjustment ??
      "",
    pfa_usd:
      engagementData?.financial_resourcePlan?.pfaUsd ??
      tempEngagementFormData?.pfa_usd ??
      "",
    pfa_status:
      engagementData?.financial_resourcePlan?.pfaStatus ??
      tempEngagementFormData?.pfa_status ??
      "",
    subcontractor_usd: parseInt(subcontractoSum) ?? "",
    status:
      engagementData?.jobCloser?.status ??
      tempEngagementFormData?.status ??
      "Not Started",
    conversion_rate:
      engagementData?.financial_resourcePlan?.conversion_rate ??
      tempEngagementFormData?.conversion_rate ??
      1,
    resourceList: tempEngagementFormData?.resourceList ?? [],
    subcontractList: tempEngagementFormData?.subcontractList ?? [],
    closeddate:
      engagementData?.jobCloser?.status === "closed"
        ? engagementData?.closeDate
          ? moment(engagementData?.closeDate, "DD-MM-YYYY")
          : tempEngagementFormData?.closeddate
            ? moment(tempEngagementFormData?.closeddate, "DD-MM-YYYY")
            : moment()
        : engagementData?.closeDate
          ? moment(engagementData?.closeDate, "DD-MM-YYYY")
          : tempEngagementFormData?.closeddate
            ? moment(tempEngagementFormData?.closeddate, "DD-MM-YYYY")
            : moment(),
    closedby:
      userList?.find((val) => val?.userid === engagementData?.closedBy)
        ?.fullname ?? "",
    close_comment: engagementData?.closedComments ?? "",
  };
};

const formsToAttributes = (values, tempEngagementFormData, form) => {
  if (values.client && !values.client_name) {
  }
  return {
    clientId: values.client,
    engagementId: values.engagement_id,
    client: {
      proposalNumber: values.proposal_number,
      managingOffice: values?.managing_office,
      department: values.department,
      competency: values.client_competency,
      clientId: values.client,
      serviceLine: values.service_line,
      clientFyMonth: values.client_fy_month,
      loeRefNo: values.loe_ref_no,
      aptengagementid: values.apt_engagement_id ?? null,
      clientName: values.client_name,
    },
    information: {
      jobTitle: values.job_title,
      classification: values.classification,
      approvedDate: values.approved_date
        ? moment(values.approved_date).format("DD-MM-YYYY")
        : "",
      stage: values.stage,
      country: values.country,
      objective: values.objective,
    },
    stakeHolder: {
      partner: values.partner,
      relationshipPartner: values.relationship_partner,
      personIncharge: values.person_incharge,
      referringBdoFirm: values.referring_bdo_firm,
      otherSideEntityName: values.other_side_entity_name,
      sentToReferral: values.sent_to_referaal,
      isauditor: values.isauditor,
      planStartDate: values.plan_start_date
        ? moment(values.plan_start_date).format("DD-MM-YYYY")
        : "",
      planEndDate: values.plan_end_date
        ? moment(values.plan_end_date).format("DD-MM-YYYY")
        : "",
      clientFyStart: values.client_fy_start
        ? moment(values.client_fy_start).format("DD-MM-YYYY")
        : "",
      clientFyEnd: values.client_fy_end
        ? moment(values.client_fy_end).format("DD-MM-YYYY")
        : "",
    },
    financial_resourcePlan: {
      billingOffice: values.billing_office,
      financialYear: values.financial_year,
      mandays: values.mandays,
      currency: values.currency,
      billingAmount: values.billing_amount,
      usdAmount:
        parseFloat(values.billing_amount) * parseFloat(values?.conversion_rate),
      plannedFeeAdjustment: values.planned_fee_adjustment,
      pfaUsd: form.getFieldValue("pfa_usd"),
      pfaStatus: values.pfa_status,
      subcontractorUsd: parseInt(values.subcontractor_usd),
      conversion_rate: values?.conversion_rate,
    },
    jobCloser: {
      status: values.status,
    },
    // Only Partner and Person Incharge are mandatory for this check.
    // If either is missing, this save is still an incomplete/draft view of the engagement.
    isDraftView: !(values.partner && values.person_incharge),
    closeDate: moment(values.closeddate).format("DD-MM-YYYY"),
    closedBy: values.closedby,
    closedComments: values.close_comment,
    loeDocument: values.loe_document_upload || [],
    coiDocument: values.coi_document_upload || [],
    resourceList: tempEngagementFormData?.resourceList ?? [],
    subcontractList: tempEngagementFormData?.subcontractList ?? [],
  };
};